import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, Plus, Search, FileText, Clock, CheckCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

interface Complaint {
  id: string;
  title: string;
  description: string;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'critical';
  category: string;
  client_name: string;
  assigned_to: string;
  created_at: string;
  updated_at: string;
}

export default function ComplaintManagementPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isNewComplaintOpen, setIsNewComplaintOpen] = useState(false);
  const [newComplaint, setNewComplaint] = useState({
    title: "",
    description: "",
    category: "",
    priority: "medium",
    client_name: "",
  });

  const queryClient = useQueryClient();

  const { data: complaints = [], isLoading } = useQuery({
    queryKey: ['complaints'],
    queryFn: async () => {
      // Direct query since table exists in DB but not in types yet
      const { data, error } = await supabase
        .from('complaints' as any)
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return (data as unknown as Complaint[]) || [];
    }
  });

  const createComplaintMutation = useMutation({
    mutationFn: async (complaint: any) => {
      const { data, error } = await supabase
        .from('complaints' as any)
        .insert([complaint])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['complaints'] });
      setIsNewComplaintOpen(false);
      setNewComplaint({ title: "", description: "", category: "", priority: "medium", client_name: "" });
      toast.success("تم إنشاء الشكوى بنجاح");
    },
    onError: () => {
      toast.error("حدث خطأ في إنشاء الشكوى");
    }
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase
        .from('complaints' as any)
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['complaints'] });
      toast.success("تم تحديث حالة الشكوى");
    }
  });

  const filteredComplaints = complaints.filter(complaint => {
    const matchesSearch = complaint.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         complaint.client_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || complaint.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-red-100 text-red-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleCreateComplaint = () => {
    if (!newComplaint.title || !newComplaint.description || !newComplaint.client_name) {
      toast.error("يرجى ملء جميع الحقول المطلوبة");
      return;
    }
    
    createComplaintMutation.mutate({
      ...newComplaint,
      status: 'open',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    });
  };

  return (
    <div className="container mx-auto p-4 md:p-6 max-w-7xl">
      <div className="flex items-center gap-4 mb-8">
        <div className="w-12 h-12 bg-gradient-to-br from-red-500 to-orange-600 rounded-lg flex items-center justify-center shadow-lg">
          <AlertTriangle className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">إدارة الشكاوى</h1>
          <p className="text-muted-foreground mt-1">
            نظام متابعة المشاكل والشكاوى من العملاء
          </p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            placeholder="البحث في الشكاوى..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pr-10"
          />
        </div>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[200px]">
            <SelectValue placeholder="تصفية حسب الحالة" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">جميع الحالات</SelectItem>
            <SelectItem value="open">مفتوحة</SelectItem>
            <SelectItem value="in_progress">قيد المعالجة</SelectItem>
            <SelectItem value="resolved">محلولة</SelectItem>
            <SelectItem value="closed">مغلقة</SelectItem>
          </SelectContent>
        </Select>

        <Dialog open={isNewComplaintOpen} onOpenChange={setIsNewComplaintOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 ml-2" />
              شكوى جديدة
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>إنشاء شكوى جديدة</DialogTitle>
              <DialogDescription>
                أدخل تفاصيل الشكوى الجديدة
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">عنوان الشكوى *</label>
                <Input
                  value={newComplaint.title}
                  onChange={(e) => setNewComplaint({ ...newComplaint, title: e.target.value })}
                  placeholder="أدخل عنوان الشكوى"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">اسم العميل *</label>
                <Input
                  value={newComplaint.client_name}
                  onChange={(e) => setNewComplaint({ ...newComplaint, client_name: e.target.value })}
                  placeholder="أدخل اسم العميل"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">الفئة</label>
                  <Select value={newComplaint.category} onValueChange={(value) => setNewComplaint({ ...newComplaint, category: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="اختر الفئة" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="service">خدمة العملاء</SelectItem>
                      <SelectItem value="booking">الحجوزات</SelectItem>
                      <SelectItem value="payment">المدفوعات</SelectItem>
                      <SelectItem value="technical">مشكلة تقنية</SelectItem>
                      <SelectItem value="other">أخرى</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">الأولوية</label>
                  <Select value={newComplaint.priority} onValueChange={(value) => setNewComplaint({ ...newComplaint, priority: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">منخفضة</SelectItem>
                      <SelectItem value="medium">متوسطة</SelectItem>
                      <SelectItem value="high">عالية</SelectItem>
                      <SelectItem value="critical">حرجة</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">وصف المشكلة *</label>
                <Textarea
                  value={newComplaint.description}
                  onChange={(e) => setNewComplaint({ ...newComplaint, description: e.target.value })}
                  placeholder="أدخل وصف تفصيلي للمشكلة"
                  rows={4}
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button 
                  onClick={handleCreateComplaint}
                  disabled={createComplaintMutation.isPending}
                >
                  {createComplaintMutation.isPending ? "جاري الإنشاء..." : "إنشاء الشكوى"}
                </Button>
                <Button variant="outline" onClick={() => setIsNewComplaintOpen(false)}>
                  إلغاء
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="text-center py-8">جاري التحميل...</div>
      ) : (
        <div className="grid gap-4">
          {filteredComplaints.map((complaint) => (
            <Card key={complaint.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg mb-2">{complaint.title}</CardTitle>
                    <CardDescription>
                      العميل: {complaint.client_name} • الفئة: {complaint.category}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Badge className={getPriorityColor(complaint.priority)}>
                      {complaint.priority === 'low' && 'منخفضة'}
                      {complaint.priority === 'medium' && 'متوسطة'}
                      {complaint.priority === 'high' && 'عالية'}
                      {complaint.priority === 'critical' && 'حرجة'}
                    </Badge>
                    <Badge className={getStatusColor(complaint.status)}>
                      {complaint.status === 'open' && 'مفتوحة'}
                      {complaint.status === 'in_progress' && 'قيد المعالجة'}
                      {complaint.status === 'resolved' && 'محلولة'}
                      {complaint.status === 'closed' && 'مغلقة'}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">{complaint.description}</p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {new Date(complaint.created_at).toLocaleDateString('ar-SA')}
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    {complaint.status !== 'closed' && (
                      <Select 
                        value={complaint.status} 
                        onValueChange={(value) => updateStatusMutation.mutate({ id: complaint.id, status: value })}
                      >
                        <SelectTrigger className="w-[140px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="open">مفتوحة</SelectItem>
                          <SelectItem value="in_progress">قيد المعالجة</SelectItem>
                          <SelectItem value="resolved">محلولة</SelectItem>
                          <SelectItem value="closed">مغلقة</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          
          {filteredComplaints.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              لا توجد شكاوى مطابقة للبحث
            </div>
          )}
        </div>
      )}
    </div>
  );
}